import React, { useMemo, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icons from '../Icons';
import { formatDateTime, parseAttendanceDescription } from './timelineUtils';

const iconMap = {
  Attendance: Icons.Calendar,
  Safeguarding: Icons.Shield,
  Medical: Icons.Document,
  Police: Icons.Alert,
  Incident: Icons.Alert,
  Inventory: Icons.Inventory,
  Status: Icons.Bell
};

const riskConfig = {
  critical: {
    label: 'Critical',
    bar: 'var(--sp-red)',
    icon: Icons.Alert
  },
  medium: {
    label: 'Medium',
    bar: 'var(--sp-orange)',
    icon: Icons.Alert
  },
  low: {
    label: 'Low',
    bar: 'var(--sp-border)',
    icon: Icons.Check
  }
};

export default function TimelineEventCard({ event }) {
  const [expanded, setExpanded] = useState(false);
  const risk = riskConfig[event.risk] || riskConfig.low;
  const CategoryIcon = iconMap[event.category] || Icons.Document;

  const summary = useMemo(() => {
    if (event.category === 'Attendance') {
      const meta = parseAttendanceDescription(event.description);
      const programme = meta.programme ? ` (${meta.programme})` : '';
      return `Attendance: ${meta.status || 'recorded'}${programme}`;
    }
    return event.description || `${event.category} event`;
  }, [event]);

  const meta = useMemo(() => {
    const items = [];
    items.push({ label: 'Date', value: formatDateTime(event.date) });
    if (event.sourceId) items.push({ label: 'Source ID', value: event.sourceId });
    if (event.category === 'Attendance') {
      const attendance = parseAttendanceDescription(event.description);
      if (attendance.status) items.push({ label: 'Status', value: attendance.status });
      if (attendance.programme) items.push({ label: 'Programme', value: attendance.programme });
    }
    items.push({ label: 'Logged by', value: event.loggedBy || 'System' });
    return items;
  }, [event]);

  const details = useMemo(() => {
    const items = [];
    if (event.category === 'Attendance') {
      const attendance = parseAttendanceDescription(event.description);
      if (attendance.notes) items.push({ label: 'Notes', value: attendance.notes });
    } else if (event.description) {
      items.push({ label: 'Notes', value: event.description });
    }
    if (event.type) items.push({ label: 'Type', value: event.type });
    return items;
  }, [event]);

  return (
    <article
      className={`sp-panel sp-event-card p-4 ${event.muted ? 'sp-event-card--muted' : ''}`}
      style={{
        borderLeft: `4px solid ${risk.bar}`
      }}
      aria-label={`${event.category} timeline event`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div aria-hidden="true" className="mt-1 text-slate-600">
            <CategoryIcon />
          </div>
          <div>
            <div className="sp-event-kicker">{event.category}</div>
            <h4 className="sp-event-title">
              {event.type || event.category} -{' '}
              <span className={`sp-risk-text sp-risk-text--${event.risk || 'low'}`}>{risk.label}</span>
            </h4>
            <p className="text-sm sp-muted mt-2">{summary}</p>
            <div className="sp-event-tags">
              <Badge
                variant={event.risk === 'critical' ? 'critical' : event.risk === 'medium' ? 'medium' : 'low'}
                className="sp-badge--prominent"
              >
                {risk.label}
              </Badge>
              {event.category === 'Attendance' && event.attendanceMeta?.status && (
                <Badge variant="inactive">{event.attendanceMeta.status}</Badge>
              )}
            </div>
            <div className="sp-event-meta">
              {meta.map((item) => (
                <span key={item.label}>
                  <span className="sp-meta-label">{item.label}</span>
                  <span>{item.value}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="sp-actions">
          {event.links?.map((link) => (
            <Button key={link.label} size="sm" variant="secondary" type="button" onClick={link.onClick}>
              {link.label}
            </Button>
          ))}
          {(details.length > 0 || event.links?.length) && (
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-controls={`details-${event.id}`}
            >
              {expanded ? 'Hide details' : 'Details'}
            </Button>
          )}
        </div>
      </div>
      {expanded && details.length > 0 && (
        <div id={`details-${event.id}`} className="sp-event-details" role="region">
          {details.map((item) => (
            <div key={item.label} className="sp-event-details__item">
              <span className="sp-meta-label">{item.label}</span>
              <span>{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}
